/* eslint-disable camelcase */
import { useRef, useEffect, useMemo } from 'react';

import config from 'config';
import { useRouter } from 'next/router';
import { useDispatch, useSelector } from 'react-redux';

import { getMembership } from '~/techstyle-shared/react-accounts';
import {
  AttributeFieldTypes,
  useProductDetailContext,
} from '~/techstyle-shared/react-components';
import { trackProductDetailViewed } from '~/techstyle-shared/react-products';
import { getSessionVisitorId, useSession } from '~/techstyle-shared/redux-core';

const getFieldChange = ({ selectionState, previousSelectionState, field }) => {
  let didChange = false;
  const value = selectionState.selectedAttributes?.[field]?.value;
  const oldValue = previousSelectionState?.selectedAttributes?.[field]?.value;

  if (value !== oldValue) {
    didChange = true;
  }

  return {
    didChange,
    value,
    oldValue,
  };
};

export default function useProductDetailEvents() {
  const { selectionStateByProductIndex, bundleProducts, product, isBundle } =
    useProductDetailContext();
  const previousProductSelectionArray = useRef([]);
  const previousPermalink = useRef();
  const dispatch = useDispatch();
  const router = useRouter();
  const membershipBrandId = config.get('public.brand.id');
  const visitorId = useSelector(getSessionVisitorId);

  const currentPermalink = useMemo(
    () => product.permalink,
    [product.permalink]
  );

  const countryCodeByStoreGroupId = config.get(
    'public.intl.countryCodeByStoreGroupId'
  );
  const reduxState = useSelector((state) => state);
  const {
    customer: { gender },
    storeGroup: { storeGroupId },
    customerDetails: { isScrubs, is_yitty: isYitty },
  } = reduxState;
  const { membershipLevelGroup } = getMembership(reduxState);
  const { isLoggedIn, dmGatewayCode } = useSession();
  const {
    query: { utm_source, utm_campaign, utm_medium },
  } = useRouter();

  useEffect(() => {
    let track = false;
    if (previousPermalink.current !== currentPermalink) {
      track = true;

      if (previousPermalink.current === undefined) {
        track = router.query.identifier === currentPermalink;
      }

      previousPermalink.current = currentPermalink;
    } else {
      for (let i = 0; i < selectionStateByProductIndex.length; i++) {
        const singleSelectionState = selectionStateByProductIndex[i];
        const previousSelectionState = previousProductSelectionArray.current[i];
        const {
          [AttributeFieldTypes.COLOR]: colorField,
          [AttributeFieldTypes.SIZE]: sizeField,
          [AttributeFieldTypes.OTHER]: otherFields,
        } = singleSelectionState.attributeFieldTypes;

        const isValidSelection =
          (isBundle && previousSelectionState?.selectedPermalink) ||
          previousSelectionState?.selectedPermalink ===
            singleSelectionState?.selectedPermalink;

        if (isValidSelection) {
          const colorChanged =
            isBundle &&
            getFieldChange({
              selectionState: singleSelectionState,
              previousSelectionState,
              field: colorField,
            }).didChange;

          const sizeChanged = getFieldChange({
            selectionState: singleSelectionState,
            previousSelectionState,
            field: sizeField,
          }).didChange;

          const otherChanged = otherFields
            ? otherFields.some((field) => {
                return getFieldChange({
                  selectionState: singleSelectionState,
                  previousSelectionState,
                  field,
                }).didChange;
              })
            : false;

          if (colorChanged || sizeChanged || otherChanged) {
            track = true;
            break;
          }
        }
      }
      previousProductSelectionArray.current = selectionStateByProductIndex;
    }

    if (track) {
      const { styleCode: style_code, styleLabel: style_label } = product;
      dispatch(
        trackProductDetailViewed({
          product,
          bundleProducts,
          selectedProductInformation: {
            psrc: router.query.psrc,
            selectionStateByProductIndex,
          },
          additionalProperties: {
            membership_brand_id: membershipBrandId,
            membership_status: membershipLevelGroup,
            user_status_initial: membershipLevelGroup,
            is_scrubs: !!isScrubs?.value,
            is_yitty: !!isYitty?.value,
            country: countryCodeByStoreGroupId[storeGroupId],
            gender,
            loggedin_status: isLoggedIn,
            product_tag: product.tagIdList.join(','),
            dmg_entered_on: dmGatewayCode,
            utm_source,
            utm_campaign,
            utm_medium,
            style_code,
            style_label,
          },
          options: {
            integrations: {
              'Google Universal Analytics': {
                clientId: visitorId,
              },
              Heap: true,
            },
          },
        })
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [product, selectionStateByProductIndex]);

  return null;
}
